import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { FeedbackItem, FeedbackStatus } from "@edufadder/shared-types";
import { Chip, type ChipTone } from "@edufadder/ui";
import * as api from "../lib/api.js";
import { ApiError } from "../lib/api.js";
import * as s from "../styles.js";

const statusTone: Record<FeedbackStatus, ChipTone> = { new: "blue", triaged: "neutral", actioned: "green", dismissed: "neutral" };

export function FeedbackTriagePage(): JSX.Element {
  const [items, setItems] = useState<FeedbackItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  function load(): void {
    api
      .listFeedback()
      .then(setItems)
      .catch((err) => setError(err instanceof ApiError ? err.message : "Couldn't load feedback."));
  }

  useEffect(load, []);

  async function triage(id: string, status: FeedbackStatus): Promise<void> {
    await api.triageFeedback(id, { status });
    load();
  }

  return (
    <div style={s.container}>
      <h1 style={s.heading1}>Feedback</h1>
      <p style={s.mutedText}>
        Feedback is never used to directly edit evidence — it's triaged here, and a curator decides separately
        whether it warrants a new evidence version.
      </p>
      {error && <p style={s.errorText}>{error}</p>}
      {(items ?? []).map((item) => (
        <div key={item.id} style={s.card}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Chip tone={statusTone[item.status]}>{item.status}</Chip>
            <span style={s.mutedText}>{item.kind}</span>
          </div>
          {item.note && <p style={{ marginTop: 8 }}>{item.note}</p>}
          {item.claim_id && (
            <p style={s.mutedText}>
              <Link to={`/claims/${item.claim_id}`}>View claim</Link>
            </p>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            {item.status === "new" && (
              <button type="button" style={s.buttonSecondary} onClick={() => void triage(item.id, "triaged")}>
                Mark triaged
              </button>
            )}
            {item.status !== "actioned" && (
              <button type="button" style={s.buttonSecondary} onClick={() => void triage(item.id, "actioned")}>
                Mark actioned
              </button>
            )}
            {item.status !== "dismissed" && (
              <button type="button" style={s.buttonSecondary} onClick={() => void triage(item.id, "dismissed")}>
                Dismiss
              </button>
            )}
          </div>
        </div>
      ))}
      {items !== null && items.length === 0 && <p style={s.mutedText}>No feedback yet.</p>}
    </div>
  );
}
